import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@cotera/client/v0/components/ui/hover-card';
import { cn } from '@cotera/client/v0/lib/utils';
import { ChildrenProps } from '@cotera/client/app/components/utils';
import { useState } from 'react';

export type TooltipSide =
  | 'right'
  | 'top'
  | 'bottom'
  | 'left'
  | 'center'
  | 'start'
  | 'end'
  | 'auto';

const convertSideToHoverCardSide = (
  side: TooltipSide
): 'center' | 'start' | 'end' | undefined => {
  switch (side) {
    case 'auto':
      return undefined;
    case 'right':
      return 'end';
    case 'top':
      return 'center';
    case 'bottom':
      return 'center';
    case 'left':
      return 'start';
    case 'center':
      return 'center';
    case 'start':
      return 'start';
    case 'end':
      return 'end';
    default:
      return 'center';
  }
};

/**
 * Which edge of the trigger the tooltip sits on. Distinct from `side`, which
 * despite the name only picks the *alignment* along the trigger — every one of
 * the ~96 `<Tooltip>` call sites relies on that, so the meaning stays put.
 * Left unset the tooltip keeps its historical placement, below the trigger.
 */
export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left';

export const Tooltip: React.FC<
  {
    offset?: number;
    side: TooltipSide;
    placement?: TooltipPlacement;
    tooltipContent?: React.ReactNode;
    hidden?: boolean;
    className?: string;
    asChild?: boolean;
    trigger?: 'hover' | 'click';
    openDelay?: number;
    closeDelay?: number;
    onOpenChange?: (open: boolean) => void;
  } & ChildrenProps
> = ({
  side,
  placement,
  children,
  tooltipContent,
  hidden = false,
  className,
  asChild = false,
  trigger = 'hover',
  openDelay = 700,
  closeDelay = 300,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  if (hidden) {
    return children;
  }

  return (
    <HoverCard
      open={trigger === 'click' ? open : undefined}
      openDelay={openDelay}
      closeDelay={closeDelay}
      onOpenChange={onOpenChange}
    >
      <HoverCardTrigger
        asChild={asChild}
        onClick={() => {
          if (trigger === 'click') {
            setOpen(true);
          }
        }}
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        onMouseLeave={() => {
          if (trigger === 'click') {
            setOpen(false);
          }
        }}
        side={placement}
        align={convertSideToHoverCardSide(side)}
        // `w-fit` is what lets a short tooltip shrink to its text, but on its
        // own it also let a one-line sentence run the width of the viewport —
        // the hover card's own `w-64` was the only thing capping it. `max-w-sm`
        // keeps both: fit the content, wrap at a readable measure. Callers that
        // need a wider surface pass their own `max-w-*`, which wins here.
        className={cn(
          'border border-border text-sm w-fit max-w-sm p-2 bg-background break-words',
          className
        )}
      >
        {tooltipContent}
      </HoverCardContent>
    </HoverCard>
  );
};

export const TooltipBase = {
  TooltipContainer: HoverCard,
  TooltipContent: HoverCardContent,
  TooltipTrigger: HoverCardTrigger,
};
